import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert, CircularProgress, Divider, Stack,
  useMediaQuery, useTheme,
} from '@mui/material';
import { CheckCircle as OkIcon, PictureAsPdf as PdfIcon, VerifiedUser as ValidIcon } from '@mui/icons-material';
import { aprovadorApi, PanelItem, Review, ActionResult, AprovadorError, brl, dataBR } from '@/services/aprovador';

interface Props {
  token: string;
  item: PanelItem | null;
  /** Conferência já feita (página de detalhes): pula a nova consulta ao banco. */
  preReview?: Review | null;
  onClose: () => void;
  onDone: () => void;
  onSessionLost: () => void;
}

const Linha: React.FC<{ rotulo: string; valor: React.ReactNode; forte?: boolean }> = ({ rotulo, valor, forte }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
    <Typography variant="body2" color="text.secondary">{rotulo}</Typography>
    <Typography variant="body2" fontWeight={forte ? 700 : 500} textAlign="right" sx={{ wordBreak: 'break-word' }}>{valor}</Typography>
  </Box>
);

/** PIX pela API: Aprovar → conferência (recebedor resolvido pelo banco) → Confirmar → comprovante. */
const ConferirDialog: React.FC<Props> = ({ token, item, preReview, onClose, onDone, onSessionLost }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ActionResult | null>(null);

  useEffect(() => {
    if (!item) return;
    setReview(null); setError(null); setResult(null);
    if (preReview && preReview.id === item.id) { setReview(preReview); return; }
    setLoading(true);
    aprovadorApi.conferir(token, item.id)
      .then(setReview)
      .catch((e: AprovadorError) => { if (e.status === 401) onSessionLost(); else setError(e.message); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  const confirmar = async () => {
    if (!item) return;
    setPaying(true);
    setError(null);
    try {
      const r = await aprovadorApi.confirmar(token, item.id);
      setResult(r);
      onDone();
    } catch (e) {
      const err = e as AprovadorError;
      if (err.status === 401) onSessionLost();
      else { setError(err.message); if (err.code === 'pay_failed' || err.code === 'not_pending') onDone(); }
    } finally {
      setPaying(false);
    }
  };

  const fechar = () => { if (!paying) onClose(); };
  const semSaldo = review && review.saldo.disponivel != null && review.valor > review.saldo.disponivel;
  const semTeto = !!review && !!review.apiLimit && review.valor > review.apiLimit.available;
  const pelaEfi = review?.bank === 'efi' && !review.transferToEfi;
  const noBanco = pelaEfi ? 'na Efí' : 'no Sicoob';

  return (
    <Dialog open={!!item} onClose={fechar} fullWidth maxWidth="xs" fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 700 }}>{result?.ok ? 'Pagamento realizado' : 'Confira antes de pagar'}</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Consultando o recebedor no banco…</Typography>
          </Stack>
        )}

        {result?.ok && (
          <Stack spacing={2} alignItems="center" textAlign="center" sx={{ py: 2 }}>
            <OkIcon color="success" sx={{ fontSize: 64 }} />
            <Typography variant="h5" fontWeight={700}>{brl(review?.valor)}</Typography>
            <Typography variant="body2" color="text.secondary">para {review?.recebedor.nome || item?.beneficiaryName}</Typography>
            {result.comprovante?.e2e && (
              <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>E2E {result.comprovante.e2e}</Typography>
            )}
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<PdfIcon />} href={result.comprovante?.pdfUrl || '#'} target="_blank" rel="noopener noreferrer">Comprovante</Button>
              <Button startIcon={<ValidIcon />} href={result.comprovante?.validateUrl || '#'} target="_blank" rel="noopener noreferrer">Validar</Button>
            </Stack>
          </Stack>
        )}

        {review && !result?.ok && (
          <>
            <Typography variant="overline" color="text.secondary">Recebedor (informado pelo banco)</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>{review.recebedor.nome || '—'}</Typography>
            {review.recebedor.documento && <Typography variant="body2" color="text.secondary">CPF/CNPJ {review.recebedor.documento}</Typography>}
            <Divider sx={{ my: 1.5 }} />
            <Linha rotulo="Valor" valor={brl(review.valor)} forte />
            {review.recebedor.chave && <Linha rotulo="Chave PIX" valor={review.recebedor.chave} />}
            {review.dueDate && <Linha rotulo="Vencimento" valor={dataBR(review.dueDate)} />}
            {review.descricao && <Linha rotulo="Descrição" valor={review.descricao} />}
            <Divider sx={{ my: 1.5 }} />
            <Linha rotulo={`Saldo disponível ${noBanco}`} valor={review.saldo.disponivel != null ? brl(review.saldo.disponivel) : 'indisponível'} />
            {review.apiLimit && <Linha rotulo="Ainda cabe no teto da API hoje" valor={brl(review.apiLimit.available)} />}
            {item && item.beneficiaryName && review.recebedor.nome &&
              !review.recebedor.nome.toLowerCase().includes(item.beneficiaryName.toLowerCase().split(' ')[0]) && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                O nome no banco é diferente do favorecido do pedido (<b>{item.beneficiaryName}</b>). Confira antes de confirmar.
              </Alert>
            )}
            {pelaEfi && !semSaldo && (
              <Alert severity="info" sx={{ mt: 1.5 }}>Boleto e guia são pagos pela conta da <b>Efí</b>, não pela do Sicoob.</Alert>
            )}
            {semSaldo && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {pelaEfi
                  ? <>Saldo insuficiente na <b>Efí</b>, que é de onde boleto e guia são pagos. Use o botão “Transferir para a Efí” na tela inicial para levar dinheiro do Sicoob e tente de novo.</>
                  : 'Saldo insuficiente para este pagamento.'}
              </Alert>
            )}
            {semTeto && <Alert severity="error" sx={{ mt: 1.5 }}>Passa do teto diário da API. Pague no app do Sicoob.</Alert>}
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: review || loading ? 2 : 0 }}>{error}</Alert>}
        {!result?.ok && review && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            {pelaEfi
              ? 'Ao confirmar, o pagamento sai na hora da conta Efí e o grupo é avisado.'
              : 'Ao confirmar, o PIX sai na hora da conta Sicoob e o grupo é avisado.'} <b>Não dá para desfazer.</b>
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        {result?.ok ? (
          <Button fullWidth variant="contained" onClick={onClose}>Fechar</Button>
        ) : (
          <>
            <Button onClick={fechar} disabled={paying}>Cancelar</Button>
            <Button variant="contained" color="success" disabled={!review || paying || !!semSaldo || !!semTeto} onClick={confirmar}
              sx={{ flex: 1 }}>
              {paying ? <CircularProgress size={22} color="inherit" /> : `Confirmar ${review ? brl(review.valor) : ''}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConferirDialog;
